import React, { useEffect, useRef, useState } from "react";
import "./QuizGame.css";
import { isTabletWidthOnly as isTablet } from "../shared/device";
import questions from "./questions.json";
import { shuffleChoices } from "./shuffle";
import {
  Entry,
  leaderboardRows,
  loadEntries,
  normalizeName,
  rankEntries,
  saveEntries,
} from "./leaderboard";

type Phase = "start" | "register" | "leaderboard" | "playing" | "finished";

const FEEDBACK_MS = 1500;
const ADMIN_TAP_WINDOW_MS = 1500;
const ADMIN_TAP_COUNT = 5;
const MAX_NAME_LENGTH = 12;
const DIGITS = "1234567890";
const LETTER_ROWS = ["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"];
const CHOICE_LABELS = "ABCD";
const formatTime = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
};

interface QuizGameProps {
  onBackToHome: () => void;
}

export default function QuizGame({ onBackToHome }: QuizGameProps) {
  const [phase, setPhase] = useState<Phase>("start");
  const [entries, setEntries] = useState<Entry[]>(loadEntries);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  // Answer order is reshuffled every play so the correct letter can't be memorised.
  const [deck, setDeck] = useState(questions);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const [endedAt, setEndedAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [modal, setModal] = useState<"exit" | "reset" | null>(null);

  const feedbackTimeoutRef = useRef<number | null>(null);
  const adminTapCountRef = useRef(0);
  const adminTapTimeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (feedbackTimeoutRef.current !== null) window.clearTimeout(feedbackTimeoutRef.current);
      if (adminTapTimeoutRef.current !== null) window.clearTimeout(adminTapTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (phase !== "playing" || endedAt !== null) return;
    const interval = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, [phase, endedAt]);

  const typeKey = (key: string) => {
    setFormError("");
    setName((current) => (current.length >= MAX_NAME_LENGTH ? current : current + key));
  };

  const deleteKey = () => {
    setFormError("");
    setName((current) => current.slice(0, -1));
  };

  const startQuiz = () => {
    const cleanName = name.trim().replace(/\s+/g, " ");

    if (!cleanName) return setFormError("Escreve o teu nome");
    if (entries.some((e) => normalizeName(e.name) === normalizeName(cleanName))) {
      return setFormError("Nome já utilizado");
    }

    const startTime = Date.now();
    setName(cleanName);
    setDeck(questions.map((q) => shuffleChoices(q)));
    setQuestionIndex(0);
    setScore(0);
    setPicked(null);
    setStartedAt(startTime);
    setNow(startTime);
    setEndedAt(null);
    setPhase("playing");
  };

  const pickAnswer = (choice: number) => {
    if (picked !== null) return;
    setPicked(choice);
    const nextScore = score + (choice === deck[questionIndex].answer ? 1 : 0);
    setScore(nextScore);
    const isLast = questionIndex + 1 >= questions.length;
    if (isLast) setEndedAt(Date.now());

    feedbackTimeoutRef.current = window.setTimeout(() => {
      feedbackTimeoutRef.current = null;
      setPicked(null);
      if (!isLast) {
        setQuestionIndex(questionIndex + 1);
        return;
      }
      const entry: Entry = { name, score: nextScore, at: Date.now() };
      const updated = [...entries, entry];
      saveEntries(updated);
      setEntries(updated);
      setPhase("finished");
    }, FEEDBACK_MS);
  };

  const goToStart = () => {
    if (feedbackTimeoutRef.current !== null) {
      window.clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
    setModal(null);
    setPicked(null);
    setName("");
    setFormError("");
    setPhase("start");
  };

  const resetLeaderboard = () => {
    saveEntries([]);
    setEntries([]);
    setModal(null);
  };

  const handleLogoTap = () => {
    adminTapCountRef.current += 1;
    if (adminTapTimeoutRef.current !== null) window.clearTimeout(adminTapTimeoutRef.current);
    adminTapTimeoutRef.current = window.setTimeout(() => {
      adminTapCountRef.current = 0;
    }, ADMIN_TAP_WINDOW_MS);
    if (adminTapCountRef.current >= ADMIN_TAP_COUNT) {
      adminTapCountRef.current = 0;
      setModal("reset");
    }
  };

  const renderLeaderboard = (playerName?: string) => {
    const rows = leaderboardRows(entries, playerName);
    return (
      <section className="quiz-board-panel">
        <h2 className="quiz-board-title">Leaderboard</h2>
        {rows.length === 0 ? (
          <p className="quiz-board-empty">Ainda não há pontuações.</p>
        ) : (
          <ol className="quiz-board">
            {rows.map((row, i) =>
              row ? (
                <li
                  key={row.entry.name}
                  className={`quiz-board-row ${
                    playerName && normalizeName(row.entry.name) === normalizeName(playerName)
                      ? "player"
                      : ""
                  }`}>
                  <span className="quiz-board-rank">{row.rank === 1 ? "👑" : `${row.rank}º`}</span>
                  <span className="quiz-board-name">{row.entry.name}</span>
                  <span className="quiz-board-score">
                    {row.entry.score}/{questions.length}
                  </span>
                </li>
              ) : (
                <li key={`gap-${i}`} className="quiz-board-gap" aria-label="mais jogadores">
                  …
                </li>
              ),
            )}
          </ol>
        )}
      </section>
    );
  };

  if (!isTablet()) {
    return (
      <div className="unsupported-device">
        <button className="back-button" onClick={onBackToHome} title="Voltar ao início">
          ← Voltar
        </button>
        Este jogo está disponível apenas em tablets.
        <br />
        Aceda através de um dispositivo compatível ou utilize a emulação de
        dispositivo no navegador.
      </div>
    );
  }

  const question = deck[questionIndex];
  // Short answers fit the mockup's 2×2 grid; longer ones stack in one column.
  const longestChoice = Math.max(...question.choices.map((c) => c.length));
  const choicesLayout = longestChoice <= 5 ? "grid" : longestChoice <= 30 ? "list" : "list long";
  const playerRank =
    rankEntries(entries).findIndex((e) => normalizeName(e.name) === normalizeName(name)) + 1;
  const elapsed = formatTime((endedAt ?? now) - startedAt);
  return (
    <div className={`quiz ${phase === "playing" ? "question-background" : ""}`}>
      {phase === "start" ? (
        <button className="back-button" onClick={onBackToHome} title="Voltar ao início">
          ← Voltar
        </button>
      ) : null}

      {phase === "start" ? (
        <main className="quiz-screen quiz-start">
          {/* The logo is part of the background; tapping it 5 times opens the leaderboard reset. */}
          <button className="quiz-logo-tap" onClick={handleLogoTap} aria-hidden="true" tabIndex={-1} />
          <h1 className="quiz-title">Quiz Palex</h1>
          <p className="quiz-lead">
            Responde às perguntas,
            <br />
            mostra o que sabes
            <br />e sobe no leaderboard!
          </p>
          <button className="quiz-pill" onClick={() => setPhase("register")}>
            Começar
          </button>
        </main>
      ) : phase === "register" ? (
        <main className="quiz-screen quiz-register">
          <h1 className="quiz-title">Identifica-te</h1>
          <div className="quiz-field">
            <span className="quiz-field-label">Nome</span>
            <span className="quiz-field-value">{name || " "}</span>
          </div>
          <div className="quiz-error" aria-live="polite">
            {formError}
          </div>
          <div className="quiz-keyboard">
            {[DIGITS, ...LETTER_ROWS].map((row) => (
              <div key={row} className="quiz-keyboard-row">
                {row.split("").map((key) => (
                  <button key={key} className="quiz-key" onPointerDown={() => typeKey(key)}>
                    {key}
                  </button>
                ))}
              </div>
            ))}
            <div className="quiz-keyboard-row">
              <button className="quiz-key wide" onPointerDown={() => typeKey(" ")}>
                Espaço
              </button>
              <button className="quiz-key" onPointerDown={deleteKey} aria-label="Apagar">
                ⌫
              </button>
            </div>
          </div>
          <div className="quiz-actions">
            <button className="quiz-pill" onClick={startQuiz}>
              Começar
            </button>
            <button className="quiz-pill outline" onClick={() => setPhase("leaderboard")}>
              Leaderboard
            </button>
          </div>
          <button className="quiz-link" onClick={goToStart}>
            Voltar
          </button>
        </main>
      ) : phase === "leaderboard" ? (
        <main className="quiz-screen quiz-leaderboard">
          {renderLeaderboard()}
          <button className="quiz-pill small" onClick={() => setPhase("register")}>
            Voltar
          </button>
        </main>
      ) : phase === "playing" ? (
        <main className="quiz-screen quiz-playing">
          <div className="quiz-progress">
            <div className="quiz-progress-tab">
              Pergunta {questionIndex + 1} de {questions.length}
            </div>
            <div className="quiz-progress-track">
              <div className="quiz-timer">{elapsed}</div>
              <div className="quiz-segments">
                {questions.map((_, i) => (
                  <span key={i} className={`quiz-segment ${i <= questionIndex ? "done" : ""}`} />
                ))}
              </div>
            </div>
          </div>
          <div className="quiz-stage">
            <h1 className={`quiz-question ${question.question.length > 90 ? "long" : ""}`}>
              {question.question}
            </h1>
            <div className={`quiz-choices ${choicesLayout}`}>
              {question.choices.map((choice, i) => (
                <button
                  key={i}
                  className={[
                    "quiz-choice",
                    picked === i ? "picked" : "",
                    picked !== null && i === question.answer ? "correct" : "",
                    picked === i && i !== question.answer ? "wrong" : "",
                  ].join(" ")}
                  onClick={() => pickAnswer(i)}
                  disabled={picked !== null}>
                  {CHOICE_LABELS[i]}) {choice}
                </button>
              ))}
            </div>
          </div>
          <button className="quiz-pill outline small" onClick={() => setModal("exit")}>
            Sair
          </button>
        </main>
      ) : (
        <main className="quiz-screen quiz-finished">
          <div className="quiz-place">
            <span className="quiz-place-number">{playerRank}º</span>
            <span className="quiz-place-label">Lugar</span>
          </div>
          <p className="quiz-lead">Quiz concluído com sucesso!</p>
          <div className="quiz-cards">
            <div className="quiz-card">
              <span className="quiz-card-label">Pontuação</span>
              <span className="quiz-card-value">
                {score}/{questions.length}
              </span>
            </div>
            <div className="quiz-card">
              <span className="quiz-card-label">Tempo</span>
              <span className="quiz-card-value">{elapsed}</span>
            </div>
          </div>
          {renderLeaderboard(name)}
          <button className="quiz-pill small" onClick={goToStart}>
            Jogar novamente
          </button>
        </main>
      )}

      {modal ? (
        <div className="quiz-modal">
          <div className="quiz-modal-box">
            {modal === "reset" ? (
              <>
                <p>Apagar a classificação?</p>
                <p className="quiz-modal-note">
                  Todas as pontuações serão apagadas definitivamente deste dispositivo.
                </p>
                <div className="quiz-modal-actions">
                  <button className="quiz-pill small danger" onClick={resetLeaderboard}>
                    Apagar tudo
                  </button>
                  <button className="quiz-pill outline small" onClick={() => setModal(null)}>
                    Cancelar
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Sair do quiz?</p>
                <div className="quiz-modal-actions">
                  <button
                    className="quiz-pill small"
                    onClick={goToStart}>
                    Sim
                  </button>
                  <button className="quiz-pill outline small" onClick={() => setModal(null)}>
                    Não
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
